import { ChevronRight } from 'lucide-react';
import type { ReactNode } from 'react';

/**
 * One state group inside a `Surface`: a native disclosure whose open/closed
 * state the caller controls, so a page can remember it per viewer
 * (`useCollapsedGroups`) instead of resetting it on every render (issue #68).
 *
 * A count sits on the summary line itself, so a collapsed group still says
 * how many rows it holds - the whole point of grouping the page's history
 * rather than hiding it.
 */
export function StateGroup({
  label,
  count,
  hue,
  open,
  onToggle,
  children,
}: {
  label: string;
  count: number;
  hue?: string;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
}) {
  return (
    <details
      open={open}
      className="group"
      // A native `<details>` fires `onToggle` on both directions, including
      // the browser's own find-in-page auto-expand. Forwarding only the
      // transitions that actually flip `open` keeps that from silently
      // overwriting the viewer's remembered choice.
      onToggle={(event) => {
        const isOpen = (event.target as HTMLDetailsElement).open;
        if (isOpen !== open) onToggle();
      }}
    >
      <summary className="flex cursor-pointer list-none items-center gap-2 px-gutter py-2.5 select-none">
        <ChevronRight
          className="text-fg-3 size-3 shrink-0 transition-transform duration-100 group-open:rotate-90"
          aria-hidden
        />
        <span className="dy-eyebrow" style={hue ? { color: hue } : undefined}>
          {label}
        </span>
        <span className="text-fg-3 tnum text-2xs">{count}</span>
      </summary>
      {children}
    </details>
  );
}
